import React, { useRef, useEffect, useState, useCallback } from 'react';
import Hls from 'hls.js';
import { 
  Play, Pause, Volume2, VolumeX, Rewind, FastForward,
  Clock, Radio, Loader2, Maximize2
} from 'lucide-react';
import { usePlayer, StreamStatus } from '../../contexts/PlayerContext';

// Hardcoded stream URL - since it never changes
const STREAM_URL = 'https://demo.unified-streaming.com/k8s/live/stable/live.isml/.m3u8';

export default function AdaptiveMediaPlayer({ mode = 'radio' }) {
  const { state, play, pause, setVolume, toggleMute, currentTrack, dispatch } = usePlayer();
  const mediaRef = useRef(null);
  const videoContainerRef = useRef(null);
  const hlsRef = useRef(null);
  const hlsInitialized = useRef(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);
  const isOfflineRef = useRef(false);
  const reconnectAttemptRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 3;
  const [isControlsVisible, setIsControlsVisible] = useState(false);
  const controlsTimeoutRef = useRef(null);
  const lastMouseMoveRef = useRef(0);
  
  // Create and set up HLS.js instance
  const setupHls = useCallback(() => {
    const media = mediaRef.current;
    if (!media || !Hls.isSupported()) {
      console.error("Cannot initialize HLS: Missing media element or no HLS support");
      return;
    }

    // Don't attempt to setup if we've exceeded max reconnection attempts
    if (reconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
      console.log("HLS: Max reconnection attempts reached");
      return;
    }

    console.log("SETUP: Creating new HLS instance");
    
    // Set connecting state first
    dispatch({ type: 'SET_STREAM_STATUS', payload: StreamStatus.CONNECTING });
    
    // Prepare absolute URL for HLS
    let finalUrl = STREAM_URL;
    if (!finalUrl.startsWith('http') && !finalUrl.startsWith('/')) {
      const baseUrl = window.location.origin + '/';
      finalUrl = baseUrl + finalUrl;
    }
    
    console.log("SETUP: Final HLS URL:", finalUrl);
    
    try {
      // Create new HLS instance with more robust settings
      const hls = new Hls({
        debug: false,
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90,
        maxBufferLength: 5,
        maxMaxBufferLength: 5,
        manifestLoadPolicy: {
          default: {
            maxTimeToFirstByteMs: 10000,
            maxLoadTimeMs: 20000,
            timeoutRetry: {
              maxNumRetry: 1,
              retryDelayMs: 1000,
              maxRetryDelayMs: 8000
            }
          }
        }
      });
      
      // Set up HLS event handlers
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        console.log("HLS EVENT: Media attached, loading source");
        hls.loadSource(finalUrl);
      });
      
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log("HLS EVENT: Manifest parsed successfully");
        dispatch({ type: 'SET_STREAM_STATUS', payload: StreamStatus.ONLINE });
        setErrorMessage(null);
        isOfflineRef.current = false;
        reconnectAttemptRef.current = 0; // Reset reconnect attempts on success
        hlsInitialized.current = true;
        
        // Try to play if we should be playing
        if (state.isPlaying) {
          console.log("HLS: Auto-playing after manifest parsed");
          try {
            media.play().catch(err => console.error("Play failed after manifest:", err));
          } catch (err) {
            console.error("Play error after manifest:", err);
          }
        }
      });
      
      hls.on(Hls.Events.ERROR, (_, data) => {
        console.log("HLS EVENT: Error detected", data);
        
        if (data.fatal) {
          console.error("HLS EVENT: Fatal error", data.type, data.details);
          
          // Mark as offline and destroy HLS instance
          isOfflineRef.current = true;
          reconnectAttemptRef.current++;
          setErrorMessage("Stream is offline - Please try again later");
          dispatch({ type: 'SET_STREAM_STATUS', payload: StreamStatus.OFFLINE });
          
          if (hls) {
            hls.destroy();
            hlsRef.current = null;
            hlsInitialized.current = false;
          }
        }
      });
      
      // Attach to media and store reference
      hls.attachMedia(media);
      hlsRef.current = hls;
      
      return hls;
    } catch (err) {
      console.error("HLS setup error:", err);
      setErrorMessage("Failed to setup HLS: " + err.message);
      isOfflineRef.current = true;
      reconnectAttemptRef.current++;
      dispatch({ type: 'SET_STREAM_STATUS', payload: StreamStatus.OFFLINE });
      return null;
    }
  }, [dispatch, state.isPlaying]);

  // Add manual reconnection handler
  const handleReconnect = useCallback(() => {
    console.log("Attempting to reconnect...");
    
    // Reset states for reconnection
    isOfflineRef.current = false;
    reconnectAttemptRef.current = 0;
    setErrorMessage(null);
    
    // Clean up existing HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
      hlsInitialized.current = false;
    }
    
    // Try to setup new HLS instance
    setupHls();
  }, [setupHls]);

  // Effect to handle stream status changes
  useEffect(() => {
    if (state.streamStatus === StreamStatus.ONLINE) {
      // If stream comes back online, reset offline state
      isOfflineRef.current = false;
      reconnectAttemptRef.current = 0;
    }
  }, [state.streamStatus]);

  // Modify play/pause handler to handle offline state
  const handlePlayPause = useCallback(() => {
    if (isOfflineRef.current) {
      handleReconnect();
      return;
    }
    
    console.log("User clicked play/pause button");
    if (state.isPlaying) {
      pause();
    } else {
      play();
    }
  }, [state.isPlaying, play, pause, handleReconnect]);

  // Format time helper for VOD playback
  const formatTime = (seconds) => {
    const date = new Date(seconds * 1000);
    return date.toISOString().substr(11, 8).replace(/^00:/, '');
  };

  // Volume change handler
  const handleVolumeChange = useCallback((value) => {
    setVolume(value);
    if (mediaRef.current) {
      mediaRef.current.volume = value;
    }
  }, [setVolume]);

  // Toggle mute handler
  const handleToggleMute = useCallback(() => {
    toggleMute();
    if (mediaRef.current) {
      mediaRef.current.muted = !state.muted;
    }
  }, [toggleMute, state.muted]);
  
  // Skip handling for VOD mode
  const handleSkip = useCallback((seconds) => {
    const media = mediaRef.current;
    if (media && state.mode === 'vod') {
      media.currentTime += seconds;
    }
  }, [state.mode]);

  // Effect to initialize media properties when mute/volume changes
  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;
    
    media.volume = state.volume;
    media.muted = state.muted;
  }, [state.volume, state.muted]);

  // Main effect to set up HLS player - runs only once at mount
  useEffect(() => {
    console.log("MAIN EFFECT: Setting up HLS player");
    
    const media = mediaRef.current;
    if (!media || !Hls.isSupported()) {
      console.error("Cannot initialize HLS: Missing media element or no HLS support");
      return;
    }
    
    // Media event handlers
    const handleTimeUpdate = () => setCurrentTime(media.currentTime);
    const handleWaiting = () => {
      console.log("MEDIA EVENT: Waiting/buffering");
      setIsBuffering(true);
    };
    const handlePlaying = () => {
      console.log("MEDIA EVENT: Playing");
      setIsBuffering(false);
      setErrorMessage(null);
      isOfflineRef.current = false;
      dispatch({ type: 'SET_STREAM_STATUS', payload: StreamStatus.ONLINE });
    };
    const handleError = (e) => {
      console.error("MEDIA EVENT: Error event triggered", e);
      if (!media.error) return;
      
      const errorCode = media.error.code;
      const errorMessage = media.error.message;
      console.error("MEDIA EVENT: Media error:", errorCode, errorMessage);
      
      setErrorMessage(`Media error: ${errorMessage}`);
      isOfflineRef.current = true;
      dispatch({ type: 'SET_STREAM_STATUS', payload: StreamStatus.OFFLINE });
    };

    // Add event listeners
    media.addEventListener('timeupdate', handleTimeUpdate);
    media.addEventListener('waiting', handleWaiting);
    media.addEventListener('playing', handlePlaying);
    media.addEventListener('error', handleError);

    // Initial setup
    setupHls();
    
    // Cleanup function
    return () => {
      console.log("CLEANUP: Destroying HLS player");
      
      // Remove event listeners
      media.removeEventListener('timeupdate', handleTimeUpdate);
      media.removeEventListener('waiting', handleWaiting);
      media.removeEventListener('playing', handlePlaying);
      media.removeEventListener('error', handleError);
      
      // Destroy HLS instance
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
        hlsInitialized.current = false;
      }
    };
  }, []); // Remove setupHls from dependencies to prevent reinitialization

  // Effect to handle play/pause state changes
  useEffect(() => {
    const media = mediaRef.current;
    if (!media || !hlsInitialized.current) return;
    
    if (state.isPlaying) {
      console.log("PLAYER: Auto-playing due to state change");
      try {
        media.play().catch(err => {
          console.error("Play failed:", err);
        });
      } catch (err) {
        console.error("Play error:", err);
      }
    } else {
      try {
        media.pause();
      } catch (err) {
        console.error("Pause error:", err);
      }
    }
  }, [state.isPlaying]);

  // Function to handle mouse movement
  const handleMouseMove = useCallback(() => {
    lastMouseMoveRef.current = Date.now();
    setIsControlsVisible(true);
    
    // Clear existing timeout
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    // Set new timeout
    controlsTimeoutRef.current = setTimeout(() => {
      const timeSinceLastMove = Date.now() - lastMouseMoveRef.current;
      if (timeSinceLastMove >= 5000) {
        setIsControlsVisible(false);
      }
    }, 5000);
  }, []);

  // Effect to clean up timeout
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div dir="rtl" className="relative bg-white/5 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/10 overflow-hidden group">
      {/* Media container */}
      <div className="relative">
        {mode === 'radio' ? (
          <audio ref={mediaRef} className="hidden" preload="auto" crossOrigin="anonymous" />
        ) : (
          <div 
            ref={videoContainerRef} 
            className="relative aspect-video bg-gradient-to-b from-black to-gray-900"
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setIsControlsVisible(true)}
          >
            <video 
              ref={mediaRef} 
              className="w-full h-full object-contain adaptive-player"
              preload="auto" 
              crossOrigin="anonymous"
              playsInline 
            />
            
            {/* Offline message with status indicator */}
            {state.streamStatus === StreamStatus.OFFLINE && (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-8 z-40
                bg-gradient-to-b from-black/90 to-gray-900/90 backdrop-blur-sm">
                <div className="text-center space-y-6">
                  <div className="relative inline-block">
                    <Radio className="w-20 h-20 text-white/80" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">אין שידור כעת</h2>
                  <p className="text-gray-400 text-sm">מוזמנים לנסות שוב מאוחר יותר או לכתוב לנו בוואטסאפ/אינסטגרם</p>
                </div>
              </div>
            )}

            {/* Loading indicator */}
            {isBuffering && state.streamStatus !== StreamStatus.OFFLINE && (
              <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-40">
                <Loader2 className="w-12 h-12 text-white animate-spin" />
              </div>
            )}

            {/* Controls overlay - Only visible on hover or when controls are active */}
            <div className={`absolute inset-0 transition-opacity duration-300 
              ${isControlsVisible ? 'opacity-100' : 'opacity-0'}`}>
              {/* Bottom controls bar */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent">
                <div className="px-8 py-6 flex items-center justify-between gap-6">
                  {/* Left side controls */}
                  <div className="flex items-center gap-6">
                    {/* Play/Pause button */}
                    {state.streamStatus === StreamStatus.ONLINE && (
                      <button
                        onClick={handlePlayPause}
                        className="text-white/90 hover:text-white transition-colors
                                bg-white/10 hover:bg-white/20 rounded-full p-3 backdrop-blur-sm"
                      >
                        {state.isPlaying ? (
                          <Pause className="w-6 h-6" />
                        ) : (
                          <Play className="w-6 h-6 translate-x-0.5" />
                        )}
                      </button>
                    )}

                    {/* Volume controls with hover expansion */}
                    <div className="group/volume flex items-center gap-3">
                      <button 
                        onClick={handleToggleMute}
                        className="text-white/90 hover:text-white transition-colors transform scale-x-[-1]
                                bg-white/10 hover:bg-white/20 rounded-full p-3 backdrop-blur-sm"
                      >
                        {state.muted ? 
                          <VolumeX className="w-6 h-6" /> : 
                          <Volume2 className="w-6 h-6" />
                        }
                      </button>
                      <div className="overflow-hidden transition-all duration-300 w-0 group-hover/volume:w-24">
                        <input 
                          type="range"
                          min="0"
                          max="1"
                          step="0.01"
                          value={state.volume}
                          onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                          className="w-24 accent-white/70 hover:accent-white transition-all"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right side controls */}
                  <div className="flex items-center gap-3">
                    {/* Fullscreen button */}
                    <button
                      onClick={() => {
                        const playerElement = videoContainerRef.current;
                        if (playerElement) {
                          if (document.fullscreenElement) {
                            document.exitFullscreen();
                          } else {
                            playerElement.requestFullscreen();
                          }
                        }
                      }}
                      className="text-white/90 hover:text-white transition-colors
                              bg-white/10 hover:bg-white/20 rounded-full p-3 backdrop-blur-sm"
                    >
                      <Maximize2 className="w-6 h-6" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}